import type { PlantCategory } from '@/types';

interface PlantAvatarProps {
  speciesScientificName?: string | null;
  category?: PlantCategory | null;
  size?: 'sm' | 'md' | 'lg';
  customSize?: number;
  status?: 'ok' | 'soon' | 'overdue' | 'critical';
  className?: string;
}

const sizeMap = { sm: 40, md: 64, lg: 96 };
const svgScale = { sm: 24, md: 36, lg: 56 };

// =================== Existing unique SVGs ===================

function FicusSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 40V28" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M24 32L20 36" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <ellipse cx="24" cy="20" rx="12" ry="14" fill="#4ade80" opacity="0.5" />
      <ellipse cx="20" cy="18" rx="7" ry="9" fill="#22c55e" />
      <ellipse cx="28" cy="17" rx="6" ry="8" fill="#16a34a" />
      <ellipse cx="24" cy="14" rx="5" ry="7" fill="#4ade80" />
      <ellipse cx="22" cy="22" rx="4" ry="5" fill="#15803d" opacity="0.6" />
    </svg>
  );
}

function MonsteraSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 42V30" stroke="#166534" strokeWidth="2" strokeLinecap="round" />
      <path d="M18 38L18 28" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M10 22C10 12 18 6 24 8C30 6 38 12 38 22C38 30 32 34 24 32C16 34 10 30 10 22Z" fill="#22c55e" />
      <ellipse cx="18" cy="18" rx="3" ry="4" fill="#e8f5e9" opacity="0.6" />
      <ellipse cx="30" cy="16" rx="2.5" ry="3.5" fill="#e8f5e9" opacity="0.6" />
      <ellipse cx="24" cy="24" rx="2" ry="3" fill="#e8f5e9" opacity="0.5" />
      <path d="M24 10V26" stroke="#15803d" strokeWidth="0.8" opacity="0.5" />
      <path d="M16 16L24 20" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
      <path d="M32 16L24 20" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
    </svg>
  );
}

function SansevieriaSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 42C24 42 24 10 24 8" stroke="#15803d" strokeWidth="3" strokeLinecap="round" />
      <path d="M20 42C20 42 18 14 19 10" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" />
      <path d="M28 42C28 42 30 14 29 10" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" />
      <path d="M16 42C16 42 14 20 16 14" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="17" y1="20" x2="22" y2="20" stroke="#86efac" strokeWidth="0.8" opacity="0.6" />
      <line x1="26" y1="18" x2="31" y2="18" stroke="#86efac" strokeWidth="0.8" opacity="0.6" />
      <line x1="18" y1="26" x2="22" y2="26" stroke="#86efac" strokeWidth="0.8" opacity="0.6" />
      <line x1="26" y1="24" x2="30" y2="24" stroke="#86efac" strokeWidth="0.8" opacity="0.6" />
      <line x1="22" y1="16" x2="26" y2="16" stroke="#86efac" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function SpathiphyllumSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 42V22" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M20 42C20 42 16 28 18 20C20 14 22 18 24 22" stroke="#22c55e" strokeWidth="0" fill="#22c55e" />
      <path d="M28 42C28 42 32 28 30 20C28 14 26 18 24 22" stroke="#16a34a" strokeWidth="0" fill="#16a34a" />
      <path d="M14 38C14 38 10 26 14 18C16 14 20 20 22 26" fill="#15803d" opacity="0.7" />
      <path d="M28 10L28 18" stroke="#166534" strokeWidth="1" strokeLinecap="round" />
      <ellipse cx="28" cy="10" rx="4" ry="6" fill="white" opacity="0.9" />
      <ellipse cx="28" cy="10" rx="1" ry="4" fill="#facc15" opacity="0.7" />
    </svg>
  );
}

function ChlorophytumSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 36C24 36 24 24 24 20" stroke="#22c55e" strokeWidth="1.5" />
      <path d="M24 24Q12 16 8 22" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 24Q36 16 40 22" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 22Q14 10 10 16" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 22Q34 10 38 16" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 20Q18 8 16 12" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 20Q30 8 32 12" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M10 22Q6 30 8 36" stroke="#86efac" strokeWidth="1" fill="none" strokeLinecap="round" />
      <circle cx="8" cy="36" r="2.5" fill="#4ade80" opacity="0.6" />
    </svg>
  );
}

function PothosSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M18 38L16 44H32L30 38Z" fill="#a78bfa" opacity="0.3" />
      <rect x="16" y="36" width="16" height="3" rx="1" fill="#a78bfa" opacity="0.4" />
      <path d="M24 36Q20 30 22 24Q24 18 20 14Q18 10 22 8" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M22 8C20 6 18 8 20 10C22 8 24 6 22 8Z" fill="#4ade80" />
      <path d="M20 14C18 12 16 14 18 16C20 14 22 12 20 14Z" fill="#22c55e" />
      <path d="M22 24C20 22 18 24 20 26C22 24 24 22 22 24Z" fill="#16a34a" />
      <path d="M20 20C18 18 16 20 18 22C20 20 22 18 20 20Z" fill="#4ade80" opacity="0.8" />
      <path d="M24 30C22 28 20 30 22 32C24 30 26 28 24 30Z" fill="#22c55e" opacity="0.8" />
    </svg>
  );
}

function DracaenaSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 42V16" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M24 30Q22 26 24 22" stroke="#166534" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 16L18 8" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 16L30 8" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 16L14 12" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 16L34 12" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 16L12 16" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M24 16L36 16" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M24 16L16 20" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M24 16L32 20" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ZamioculcasSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M20 42V12" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M28 42V14" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
      <ellipse cx="16" cy="16" rx="4" ry="2.5" fill="#22c55e" transform="rotate(-20 16 16)" />
      <ellipse cx="16" cy="22" rx="4" ry="2.5" fill="#4ade80" transform="rotate(-15 16 22)" />
      <ellipse cx="17" cy="28" rx="4" ry="2.5" fill="#22c55e" transform="rotate(-10 17 28)" />
      <ellipse cx="18" cy="34" rx="4" ry="2.5" fill="#16a34a" transform="rotate(-5 18 34)" />
      <ellipse cx="32" cy="18" rx="4" ry="2.5" fill="#16a34a" transform="rotate(20 32 18)" />
      <ellipse cx="32" cy="24" rx="4" ry="2.5" fill="#22c55e" transform="rotate(15 32 24)" />
      <ellipse cx="31" cy="30" rx="4" ry="2.5" fill="#4ade80" transform="rotate(10 31 30)" />
      <ellipse cx="30" cy="36" rx="4" ry="2.5" fill="#22c55e" transform="rotate(5 30 36)" />
    </svg>
  );
}

function AloeSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 40L24 10L20 40Z" fill="#22c55e" />
      <path d="M24 40L16 14L14 40Z" fill="#16a34a" />
      <path d="M24 40L32 14L34 40Z" fill="#16a34a" />
      <path d="M24 40L10 22L12 40Z" fill="#15803d" opacity="0.8" />
      <path d="M24 40L38 22L36 40Z" fill="#15803d" opacity="0.8" />
      <path d="M24 40L24 10L28 40Z" fill="#4ade80" opacity="0.6" />
      <circle cx="20" cy="20" r="0.8" fill="#dcfce7" opacity="0.7" />
      <circle cx="28" cy="20" r="0.8" fill="#dcfce7" opacity="0.7" />
      <circle cx="18" cy="26" r="0.8" fill="#dcfce7" opacity="0.7" />
      <circle cx="30" cy="26" r="0.8" fill="#dcfce7" opacity="0.7" />
      <circle cx="16" cy="32" r="0.8" fill="#dcfce7" opacity="0.7" />
      <circle cx="32" cy="32" r="0.8" fill="#dcfce7" opacity="0.7" />
    </svg>
  );
}

function CalateaSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 42V26" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M18 40V28" stroke="#166534" strokeWidth="1.2" strokeLinecap="round" />
      <ellipse cx="24" cy="18" rx="8" ry="12" fill="#22c55e" />
      <ellipse cx="16" cy="22" rx="6" ry="10" fill="#16a34a" />
      <path d="M24 8V28" stroke="#15803d" strokeWidth="1" opacity="0.5" />
      <path d="M20 12Q24 18 20 28" stroke="#4ade80" strokeWidth="0.8" opacity="0.5" fill="none" />
      <path d="M28 12Q24 18 28 28" stroke="#4ade80" strokeWidth="0.8" opacity="0.5" fill="none" />
      <path d="M16 14V30" stroke="#15803d" strokeWidth="0.8" opacity="0.4" />
      <path d="M13 18Q16 22 13 30" stroke="#4ade80" strokeWidth="0.6" opacity="0.4" fill="none" />
      <path d="M19 18Q16 22 19 30" stroke="#4ade80" strokeWidth="0.6" opacity="0.4" fill="none" />
    </svg>
  );
}

function GenericLeafSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      <path d="M24 42V22" stroke="#166534" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 20C12 10 20 4 28 8C36 12 36 24 28 28C20 32 12 28 12 20Z" fill="#22c55e" />
      <path d="M20 12L26 24" stroke="#15803d" strokeWidth="0.8" opacity="0.5" />
      <path d="M16 18L24 22" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
      <path d="M22 8L26 20" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
    </svg>
  );
}

// =================== Category SVGs ===================

function SucculentSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Rosette succulent */}
      <ellipse cx="24" cy="28" rx="14" ry="8" fill="#22c55e" opacity="0.3" />
      <ellipse cx="24" cy="24" rx="6" ry="10" fill="#4ade80" />
      <ellipse cx="16" cy="26" rx="5" ry="8" fill="#22c55e" transform="rotate(20 16 26)" />
      <ellipse cx="32" cy="26" rx="5" ry="8" fill="#22c55e" transform="rotate(-20 32 26)" />
      <ellipse cx="12" cy="30" rx="4" ry="6" fill="#16a34a" transform="rotate(35 12 30)" />
      <ellipse cx="36" cy="30" rx="4" ry="6" fill="#16a34a" transform="rotate(-35 36 30)" />
      <ellipse cx="24" cy="20" rx="3" ry="5" fill="#86efac" opacity="0.6" />
    </svg>
  );
}

function CactusSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Main body */}
      <rect x="20" y="12" width="8" height="28" rx="4" fill="#22c55e" />
      {/* Left arm */}
      <rect x="10" y="20" width="6" height="14" rx="3" fill="#16a34a" />
      <rect x="13" y="14" width="6" height="10" rx="3" fill="#16a34a" />
      {/* Right arm */}
      <rect x="32" y="24" width="6" height="10" rx="3" fill="#4ade80" />
      <rect x="29" y="18" width="6" height="10" rx="3" fill="#4ade80" />
      {/* Spines */}
      <line x1="20" y1="18" x2="18" y2="16" stroke="#15803d" strokeWidth="0.6" />
      <line x1="28" y1="22" x2="30" y2="20" stroke="#15803d" strokeWidth="0.6" />
      <line x1="20" y1="28" x2="18" y2="28" stroke="#15803d" strokeWidth="0.6" />
      <line x1="28" y1="32" x2="30" y2="32" stroke="#15803d" strokeWidth="0.6" />
      {/* Flower on top */}
      <circle cx="24" cy="11" r="2.5" fill="#f472b6" opacity="0.8" />
      <circle cx="24" cy="11" r="1" fill="#fbbf24" />
    </svg>
  );
}

function FernSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Central stem */}
      <path d="M24 42V10" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
      {/* Fronds - left */}
      <path d="M24 14Q16 10 10 14" stroke="#4ade80" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 20Q14 16 8 20" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 26Q14 22 8 28" stroke="#16a34a" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 32Q16 28 10 34" stroke="#15803d" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Fronds - right */}
      <path d="M24 14Q32 10 38 14" stroke="#4ade80" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 20Q34 16 40 20" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 26Q34 22 40 28" stroke="#16a34a" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 32Q32 28 38 34" stroke="#15803d" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Small leaflets */}
      <circle cx="12" cy="13" r="1.5" fill="#4ade80" opacity="0.5" />
      <circle cx="36" cy="13" r="1.5" fill="#4ade80" opacity="0.5" />
      <circle cx="10" cy="19" r="1.5" fill="#22c55e" opacity="0.5" />
      <circle cx="38" cy="19" r="1.5" fill="#22c55e" opacity="0.5" />
    </svg>
  );
}

function PalmSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Trunk */}
      <path d="M24 42V18" stroke="#a3733a" strokeWidth="3" strokeLinecap="round" />
      <path d="M23 30Q24 28 25 30" stroke="#8B6914" strokeWidth="0.5" opacity="0.4" />
      <path d="M23 34Q24 32 25 34" stroke="#8B6914" strokeWidth="0.5" opacity="0.4" />
      {/* Palm fronds */}
      <path d="M24 18Q14 8 6 14" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 18Q34 8 42 14" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 18Q10 14 4 22" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 18Q38 14 44 22" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 18Q18 6 16 10" stroke="#4ade80" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 18Q30 6 32 10" stroke="#4ade80" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function FloweringSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Stem */}
      <path d="M24 42V20" stroke="#166534" strokeWidth="2" strokeLinecap="round" />
      {/* Leaves */}
      <path d="M24 32Q18 28 16 32Q18 36 24 32Z" fill="#22c55e" />
      <path d="M24 28Q30 24 32 28Q30 32 24 28Z" fill="#16a34a" />
      {/* Flower petals */}
      <ellipse cx="24" cy="14" rx="4" ry="6" fill="#f472b6" opacity="0.8" />
      <ellipse cx="18" cy="17" rx="4" ry="6" fill="#fb7185" opacity="0.7" transform="rotate(60 18 17)" />
      <ellipse cx="30" cy="17" rx="4" ry="6" fill="#fb7185" opacity="0.7" transform="rotate(-60 30 17)" />
      <ellipse cx="20" cy="22" rx="4" ry="5" fill="#f472b6" opacity="0.6" transform="rotate(30 20 22)" />
      <ellipse cx="28" cy="22" rx="4" ry="5" fill="#f472b6" opacity="0.6" transform="rotate(-30 28 22)" />
      {/* Center */}
      <circle cx="24" cy="18" r="3" fill="#fbbf24" />
      <circle cx="24" cy="18" r="1.5" fill="#f59e0b" />
    </svg>
  );
}

function VineSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Hanging vines */}
      <path d="M16 6Q12 16 14 26Q16 36 12 44" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 4Q20 14 22 24Q24 34 20 42" stroke="#16a34a" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M32 6Q28 16 30 26Q32 36 28 44" stroke="#4ade80" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Heart-shaped leaves */}
      <path d="M14 12C12 10 10 12 12 14C14 12 16 10 14 12Z" fill="#4ade80" />
      <path d="M22 10C20 8 18 10 20 12C22 10 24 8 22 10Z" fill="#22c55e" />
      <path d="M30 12C28 10 26 12 28 14C30 12 32 10 30 12Z" fill="#16a34a" />
      <path d="M14 22C12 20 10 22 12 24C14 22 16 20 14 22Z" fill="#22c55e" />
      <path d="M22 20C20 18 18 20 20 22C22 20 24 18 22 20Z" fill="#4ade80" />
      <path d="M30 22C28 20 26 22 28 24C30 22 32 20 30 22Z" fill="#22c55e" />
      <path d="M14 32C12 30 10 32 12 34C14 32 16 30 14 32Z" fill="#16a34a" />
      <path d="M22 30C20 28 18 30 20 32C22 30 24 28 22 30Z" fill="#15803d" />
      <path d="M30 32C28 30 26 32 28 34C30 32 32 30 30 32Z" fill="#4ade80" />
    </svg>
  );
}

function HerbSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Pot */}
      <path d="M16 38L14 44H34L32 38Z" fill="#d4a574" opacity="0.5" />
      <rect x="14" y="36" width="20" height="3" rx="1" fill="#d4a574" opacity="0.6" />
      {/* Herb stems and leaves */}
      <path d="M20 36V18" stroke="#15803d" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M24 36V14" stroke="#166534" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M28 36V16" stroke="#15803d" strokeWidth="1.2" strokeLinecap="round" />
      {/* Oval leaves */}
      <ellipse cx="16" cy="20" rx="3" ry="2" fill="#4ade80" transform="rotate(-15 16 20)" />
      <ellipse cx="24" cy="16" rx="3" ry="2" fill="#22c55e" transform="rotate(10 24 16)" />
      <ellipse cx="32" cy="18" rx="3" ry="2" fill="#4ade80" transform="rotate(15 32 18)" />
      <ellipse cx="18" cy="26" rx="3" ry="2" fill="#22c55e" transform="rotate(-10 18 26)" />
      <ellipse cx="30" cy="24" rx="3" ry="2" fill="#16a34a" transform="rotate(5 30 24)" />
      <ellipse cx="22" cy="22" rx="2.5" ry="1.5" fill="#86efac" transform="rotate(-5 22 22)" />
      <ellipse cx="26" cy="28" rx="2.5" ry="1.5" fill="#22c55e" transform="rotate(10 26 28)" />
    </svg>
  );
}

function CitrusSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Trunk */}
      <path d="M24 42V24" stroke="#8B6914" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M24 30Q20 28 18 30" stroke="#166534" strokeWidth="1" strokeLinecap="round" />
      {/* Canopy */}
      <ellipse cx="24" cy="18" rx="14" ry="12" fill="#22c55e" />
      <ellipse cx="20" cy="16" rx="8" ry="7" fill="#4ade80" opacity="0.5" />
      <ellipse cx="28" cy="20" rx="6" ry="5" fill="#16a34a" opacity="0.5" />
      {/* Fruits */}
      <circle cx="18" cy="14" r="3" fill="#fbbf24" />
      <circle cx="30" cy="16" r="2.5" fill="#facc15" />
      <circle cx="24" cy="22" r="2" fill="#fbbf24" opacity="0.8" />
      {/* Small leaf on fruit */}
      <ellipse cx="19" cy="11.5" rx="1.5" ry="0.8" fill="#15803d" transform="rotate(-20 19 11.5)" />
      <ellipse cx="31" cy="13.5" rx="1.2" ry="0.6" fill="#15803d" transform="rotate(20 31 13.5)" />
    </svg>
  );
}

// =================== New SVGs for expanded species ===================

function PeperomiaSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Compact rosette of round leaves */}
      <path d="M24 40V28" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <ellipse cx="20" cy="22" rx="7" ry="5" fill="#22c55e" transform="rotate(-15 20 22)" />
      <ellipse cx="28" cy="20" rx="7" ry="5" fill="#16a34a" transform="rotate(15 28 20)" />
      <ellipse cx="24" cy="18" rx="6" ry="5" fill="#4ade80" />
      <ellipse cx="18" cy="26" rx="5" ry="4" fill="#15803d" transform="rotate(-10 18 26)" />
      <ellipse cx="30" cy="25" rx="5" ry="4" fill="#22c55e" transform="rotate(10 30 25)" />
      <path d="M24 14V22" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
      <path d="M20 18L24 20" stroke="#15803d" strokeWidth="0.5" opacity="0.3" />
      <path d="M28 17L24 20" stroke="#15803d" strokeWidth="0.5" opacity="0.3" />
    </svg>
  );
}

function OrchidSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Orchid stem + flowers */}
      <path d="M24 42V18" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M24 18Q26 14 28 16" stroke="#166534" strokeWidth="1" strokeLinecap="round" />
      {/* Flower 1 */}
      <ellipse cx="30" cy="12" rx="4" ry="2.5" fill="#e879f9" opacity="0.8" transform="rotate(-30 30 12)" />
      <ellipse cx="32" cy="16" rx="4" ry="2.5" fill="#d946ef" opacity="0.7" transform="rotate(30 32 16)" />
      <ellipse cx="28" cy="16" rx="3" ry="2" fill="#f0abfc" opacity="0.7" transform="rotate(-20 28 16)" />
      <circle cx="30" cy="14" r="2" fill="#fbbf24" opacity="0.8" />
      {/* Flower 2 */}
      <ellipse cx="22" cy="20" rx="3" ry="2" fill="#e879f9" opacity="0.6" transform="rotate(20 22 20)" />
      <ellipse cx="20" cy="22" rx="2.5" ry="1.5" fill="#d946ef" opacity="0.5" />
      <circle cx="21" cy="21" r="1.2" fill="#fbbf24" opacity="0.6" />
      {/* Leaves at base */}
      <path d="M24 42Q18 36 16 38Q18 40 24 42Z" fill="#22c55e" />
      <path d="M24 42Q30 36 32 38Q30 40 24 42Z" fill="#16a34a" />
    </svg>
  );
}

function TillandsiaSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Air plant - spiky rosette without pot */}
      <path d="M24 36Q22 28 18 20Q16 14 20 10" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 36Q26 28 30 20Q32 14 28 10" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 36Q20 26 14 22Q10 18 12 12" stroke="#22c55e" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M24 36Q28 26 34 22Q38 18 36 12" stroke="#22c55e" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M24 36Q24 24 24 14Q24 8 24 6" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M24 36Q18 30 10 28" stroke="#15803d" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M24 36Q30 30 38 28" stroke="#15803d" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Small bloom at center */}
      <circle cx="24" cy="30" r="2" fill="#a78bfa" opacity="0.5" />
    </svg>
  );
}

function CarnivorousSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Venus flytrap style */}
      <path d="M24 42V26" stroke="#166534" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 42V30" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M28 42V28" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" />
      {/* Trap 1 - open jaw */}
      <path d="M24 18C20 14 16 16 18 20C20 18 24 18 24 18Z" fill="#22c55e" />
      <path d="M24 18C28 14 32 16 30 20C28 18 24 18 24 18Z" fill="#4ade80" />
      {/* Teeth */}
      <path d="M19 18L21 16.5M21 18.5L23 17" stroke="#15803d" strokeWidth="0.5" strokeLinecap="round" />
      <path d="M29 18L27 16.5M27 18.5L25 17" stroke="#15803d" strokeWidth="0.5" strokeLinecap="round" />
      {/* Trap 2 */}
      <path d="M20 24C16 22 14 24 16 26C18 24 20 24 20 24Z" fill="#16a34a" />
      <path d="M20 24C18 20 16 20 16 22C18 22 20 24 20 24Z" fill="#22c55e" />
      {/* Trap 3 */}
      <path d="M28 22C32 20 34 22 32 24C30 22 28 22 28 22Z" fill="#22c55e" />
      <path d="M28 22C30 18 32 18 32 20C30 20 28 22 28 22Z" fill="#4ade80" />
      {/* Red inner color */}
      <ellipse cx="24" cy="17.5" rx="2" ry="1" fill="#ef4444" opacity="0.3" />
    </svg>
  );
}

function BonsaiSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Shallow pot */}
      <rect x="14" y="38" width="20" height="3" rx="1" fill="#a3733a" opacity="0.6" />
      <path d="M16 41L15 44H33L32 41Z" fill="#8B6914" opacity="0.4" />
      {/* Trunk - curved bonsai style */}
      <path d="M22 38Q18 34 20 28Q22 24 20 20" stroke="#8B6914" strokeWidth="3" strokeLinecap="round" />
      <path d="M20 20Q18 18 22 16" stroke="#8B6914" strokeWidth="2" strokeLinecap="round" />
      {/* Branch */}
      <path d="M21 26Q28 24 32 26" stroke="#8B6914" strokeWidth="1.5" strokeLinecap="round" />
      {/* Canopy clusters */}
      <ellipse cx="22" cy="14" rx="6" ry="4" fill="#22c55e" />
      <ellipse cx="18" cy="16" rx="4" ry="3" fill="#16a34a" />
      <ellipse cx="26" cy="12" rx="4" ry="3" fill="#4ade80" />
      <ellipse cx="32" cy="24" rx="5" ry="3.5" fill="#22c55e" />
      <ellipse cx="36" cy="22" rx="3" ry="2.5" fill="#16a34a" />
    </svg>
  );
}

function StringSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* String of pearls/hearts hanging plant */}
      <rect x="14" y="4" width="20" height="4" rx="2" fill="#d4a574" opacity="0.5" />
      {/* Strings with pearls */}
      <path d="M18 8Q16 16 18 24Q20 32 18 40" stroke="#22c55e" strokeWidth="1" fill="none" />
      <path d="M24 8Q22 14 24 22Q26 30 24 38" stroke="#16a34a" strokeWidth="1" fill="none" />
      <path d="M30 8Q28 16 30 24Q32 32 30 42" stroke="#22c55e" strokeWidth="1" fill="none" />
      {/* Pearls on strings */}
      <circle cx="17" cy="12" r="2.5" fill="#4ade80" />
      <circle cx="19" cy="20" r="2.5" fill="#22c55e" />
      <circle cx="17" cy="28" r="2.5" fill="#16a34a" />
      <circle cx="19" cy="36" r="2.5" fill="#4ade80" />
      <circle cx="23" cy="14" r="2.5" fill="#22c55e" />
      <circle cx="25" cy="22" r="2.5" fill="#4ade80" />
      <circle cx="23" cy="30" r="2.5" fill="#16a34a" />
      <circle cx="29" cy="12" r="2.5" fill="#16a34a" />
      <circle cx="31" cy="20" r="2.5" fill="#22c55e" />
      <circle cx="29" cy="28" r="2.5" fill="#4ade80" />
      <circle cx="31" cy="36" r="2.5" fill="#22c55e" />
    </svg>
  );
}

function SyngoniumSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Arrow-shaped leaves */}
      <path d="M24 42V22" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M20 40V26" stroke="#166534" strokeWidth="1.2" strokeLinecap="round" />
      {/* Arrow leaf 1 */}
      <path d="M24 14L18 22L24 20L30 22Z" fill="#4ade80" />
      <path d="M24 14V20" stroke="#15803d" strokeWidth="0.8" opacity="0.5" />
      {/* Arrow leaf 2 */}
      <path d="M18 20L12 28L18 26L24 28Z" fill="#22c55e" />
      <path d="M18 20V26" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
      {/* Arrow leaf 3 */}
      <path d="M28 18L22 26L28 24L34 26Z" fill="#16a34a" />
      <path d="M28 18V24" stroke="#15803d" strokeWidth="0.6" opacity="0.4" />
      {/* Small leaf */}
      <path d="M32 22L28 28L32 27L36 28Z" fill="#4ade80" opacity="0.7" />
    </svg>
  );
}

function AdeniumSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Fat trunk (caudex) */}
      <ellipse cx="24" cy="34" rx="10" ry="6" fill="#a3733a" opacity="0.5" />
      <path d="M24 28V16" stroke="#8B6914" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 30Q18 24 20 18" stroke="#8B6914" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M28 30Q30 24 28 18" stroke="#8B6914" strokeWidth="1.5" strokeLinecap="round" />
      {/* Leaves */}
      <ellipse cx="18" cy="16" rx="3" ry="1.5" fill="#22c55e" transform="rotate(-20 18 16)" />
      <ellipse cx="30" cy="16" rx="3" ry="1.5" fill="#22c55e" transform="rotate(20 30 16)" />
      <ellipse cx="24" cy="14" rx="3" ry="1.5" fill="#4ade80" />
      {/* Flowers */}
      <circle cx="22" cy="10" r="3" fill="#f472b6" opacity="0.8" />
      <circle cx="26" cy="8" r="3" fill="#fb7185" opacity="0.8" />
      <circle cx="22" cy="10" r="1" fill="#fbbf24" />
      <circle cx="26" cy="8" r="1" fill="#fbbf24" />
    </svg>
  );
}

// =================== SVG Maps ===================

const PLANT_SVGS: Record<string, React.FC<{ className?: string }>> = {
  'Ficus benjamina': FicusSvg,
  'Ficus elastica': FicusSvg,
  'Ficus lyrata': FicusSvg,
  'Ficus retusa': BonsaiSvg,
  'Monstera deliciosa': MonsteraSvg,
  'Monstera adansonii': MonsteraSvg,
  'Sansevieria trifasciata': SansevieriaSvg,
  'Spathiphyllum': SpathiphyllumSvg,
  'Spathiphyllum wallisii': SpathiphyllumSvg,
  'Chlorophytum comosum': ChlorophytumSvg,
  'Epipremnum aureum': PothosSvg,
  'Dracaena marginata': DracaenaSvg,
  'Dracaena fragrans': DracaenaSvg,
  'Zamioculcas zamiifolia': ZamioculcasSvg,
  'Aloe vera': AloeSvg,
  'Calathea': CalateaSvg,
  // New species-level SVGs
  'Peperomia obtusifolia': PeperomiaSvg,
  'Peperomia caperata': PeperomiaSvg,
  'Pilea peperomioides': PeperomiaSvg,
  'Dendrobium': OrchidSvg,
  'Oncidium': OrchidSvg,
  'Senecio rowleyanus': StringSvg,
  'Senecio herreianus': StringSvg,
  'Ceropegia woodii': StringSvg,
  'Syngonium podophyllum': SyngoniumSvg,
  'Caladium': SyngoniumSvg,
  'Adenium obesum': AdeniumSvg,
  'Dionaea muscipula': CarnivorousSvg,
  'Drosera capensis': CarnivorousSvg,
  'Drosera rotundifolia': CarnivorousSvg,
};

function BulbSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Bulb underground */}
      <ellipse cx="24" cy="36" rx="8" ry="5" fill="#d4a574" opacity="0.4" />
      <ellipse cx="24" cy="34" rx="6" ry="6" fill="#f5e6d3" opacity="0.6" />
      {/* Stem */}
      <path d="M24 28V14" stroke="#166534" strokeWidth="2" strokeLinecap="round" />
      {/* Flower */}
      <ellipse cx="24" cy="10" rx="3" ry="5" fill="#f472b6" opacity="0.8" />
      <ellipse cx="20" cy="12" rx="3" ry="4" fill="#fb7185" opacity="0.7" transform="rotate(30 20 12)" />
      <ellipse cx="28" cy="12" rx="3" ry="4" fill="#fb7185" opacity="0.7" transform="rotate(-30 28 12)" />
      {/* Leaves */}
      <path d="M24 24Q18 20 16 24Q18 26 24 24Z" fill="#22c55e" />
      <path d="M24 20Q30 16 32 20Q30 22 24 20Z" fill="#16a34a" />
    </svg>
  );
}

function AquaticSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Water surface */}
      <path d="M4 30Q12 28 24 30Q36 32 44 30" stroke="#60a5fa" strokeWidth="1" opacity="0.5" fill="none" />
      <path d="M4 34Q12 32 24 34Q36 36 44 34" stroke="#60a5fa" strokeWidth="0.8" opacity="0.3" fill="none" />
      {/* Lily pad */}
      <ellipse cx="28" cy="28" rx="8" ry="4" fill="#22c55e" opacity="0.7" />
      <path d="M28 24V28" stroke="#15803d" strokeWidth="0.5" opacity="0.3" />
      {/* Stem + leaf */}
      <path d="M18 28V12" stroke="#166534" strokeWidth="1.5" strokeLinecap="round" />
      <ellipse cx="18" cy="10" rx="5" ry="8" fill="#22c55e" />
      <path d="M18 4V16" stroke="#15803d" strokeWidth="0.8" opacity="0.4" />
      {/* Small flower */}
      <circle cx="30" cy="24" r="2.5" fill="#f9a8d4" opacity="0.8" />
      <circle cx="30" cy="24" r="1" fill="#fbbf24" />
    </svg>
  );
}

function VegetableSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className}>
      {/* Tomato body */}
      <ellipse cx="24" cy="24" rx="13" ry="12" fill="#ef4444" />
      <ellipse cx="24" cy="24" rx="13" ry="12" fill="#dc2626" opacity="0.5" />
      {/* Segments */}
      <path d="M24 12C22 18 22 30 24 36" stroke="#b91c1c" strokeWidth="0.6" opacity="0.4" />
      <path d="M14 16C18 20 28 28 34 32" stroke="#b91c1c" strokeWidth="0.5" opacity="0.3" />
      <path d="M34 16C30 20 20 28 14 32" stroke="#b91c1c" strokeWidth="0.5" opacity="0.3" />
      {/* Highlight */}
      <ellipse cx="20" cy="19" rx="4" ry="3" fill="white" opacity="0.2" />
      {/* Stem */}
      <path d="M24 12V8" stroke="#15803d" strokeWidth="2" strokeLinecap="round" />
      {/* Leaves */}
      <path d="M24 10C20 8 18 6 20 4C22 6 24 8 24 10Z" fill="#22c55e" />
      <path d="M24 10C28 8 30 6 28 4C26 6 24 8 24 10Z" fill="#16a34a" />
      <path d="M24 11C22 10 19 10 20 8" stroke="#15803d" strokeWidth="0.8" fill="none" strokeLinecap="round" />
      <path d="M24 11C26 10 29 10 28 8" stroke="#15803d" strokeWidth="0.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}

const CATEGORY_SVGS: Record<string, React.FC<{ className?: string }>> = {
  TROPICAL: MonsteraSvg,
  SUCCULENT: SucculentSvg,
  CACTUS: CactusSvg,
  FERN: FernSvg,
  PALM: PalmSvg,
  FLOWERING: FloweringSvg,
  VINE: VineSvg,
  HERB: HerbSvg,
  CITRUS: CitrusSvg,
  DECORATIVE_LEAFY: DracaenaSvg,
  AIR_PLANT: TillandsiaSvg,
  CARNIVOROUS: CarnivorousSvg,
  BONSAI: BonsaiSvg,
  ORCHID: OrchidSvg,
  BULB: BulbSvg,
  AQUATIC: AquaticSvg,
  VEGETABLE: VegetableSvg,
};

function findSvgComponent(name?: string | null, category?: string | null): React.FC<{ className?: string }> {
  if (name) {
    // Exact match
    if (PLANT_SVGS[name]) return PLANT_SVGS[name];

    // Match by genus (first word of scientific name)
    const genus = name.split(' ')[0].toLowerCase();
    for (const [key, component] of Object.entries(PLANT_SVGS)) {
      if (key.split(' ')[0].toLowerCase() === genus) {
        return component;
      }
    }
  }

  // Category fallback
  if (category && CATEGORY_SVGS[category]) {
    return CATEGORY_SVGS[category];
  }

  return GenericLeafSvg;
}

function getAnimationClass(status?: string): string {
  switch (status) {
    case 'ok':
      return 'animate-sway';
    case 'overdue':
    case 'critical':
      return 'animate-droop';
    default:
      return '';
  }
}

export default function PlantAvatar({ speciesScientificName, category, size = 'md', customSize, status, className = '' }: PlantAvatarProps) {
  const SvgComponent = findSvgComponent(speciesScientificName, category);
  const containerSize = customSize ?? sizeMap[size];
  const iconSize = customSize ? Math.round(customSize * 0.6) : svgScale[size];
  const animClass = getAnimationClass(status);

  return (
    <div
      className={`rounded-full glass-circle-green flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: containerSize, height: containerSize }}
    >
      <div className={animClass} style={{ transformOrigin: 'bottom center', width: iconSize, height: iconSize }}>
        <SvgComponent />
      </div>
    </div>
  );
}
